import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ShieldCheck } from "lucide-react";

const tempId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function InsurancePlansEditor({ value = [], onChange }) {
  const add = () => {
    onChange([
      ...value,
      { id: tempId(), name: "", price_per_day: 10, description: "", coverage: "", exclusions: "", deposit: 0, excess: 0 },
    ]);
  };

  const update = (idx, patch) => {
    onChange(value.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const normalizeCoverage = (raw) =>
    Array.isArray(raw)
      ? raw
      : (raw || "").split(",").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="space-y-3" data-testid="insurance-editor">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Insurance plans</Label>
          <p className="text-xs text-neutral-500 mt-1 max-w-xl">
            Optional. Add one or more plans (e.g. Basic, Premium). Customers can pick one — or none — at booking. Cost is `price/day × days`.
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={add} className="rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 flex-shrink-0" data-testid="add-insurance-btn">
          <Plus className="h-4 w-4 mr-1" /> Add plan
        </Button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-xl bg-neutral-50 border border-dashed border-border p-4 text-sm text-neutral-500">
          No optional plans yet. Third-party liability insurance is always included as the minimum coverage at no extra cost — add plans here for upsells (CDW, premium, theft, etc.).
        </div>
      ) : (
        <div className="space-y-3">
          {value.map((p, i) => (
            <div key={p.id || i} className="rounded-2xl border border-border p-4 space-y-3" data-testid={`insurance-plan-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Name</Label>
                  <Input
                    className="mt-1 rounded-xl"
                    placeholder="e.g. Premium"
                    value={p.name}
                    onChange={(e) => update(i, { name: e.target.value })}
                    data-testid={`insurance-name-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Price / day (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    className="mt-1 rounded-xl"
                    value={p.price_per_day}
                    onChange={(e) => update(i, { price_per_day: e.target.value === "" ? 0 : Number(e.target.value) })}
                    data-testid={`insurance-price-${i}`}
                  />
                </div>
                <div className="flex items-end justify-end">
                  <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`insurance-remove-${i}`} aria-label="Remove plan">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Description</Label>
                <Textarea
                  rows={2}
                  className="mt-1 rounded-xl"
                  placeholder="Short summary shown to customers"
                  value={p.description || ""}
                  onChange={(e) => update(i, { description: e.target.value })}
                  data-testid={`insurance-description-${i}`}
                />
              </div>
              {/* Iter 67ah — deposit + excess per insurance plan. Vendor can
                  configure cheaper plans with higher deposits (e.g. "Minimum
                  Coverage = €500 deposit") and bundle "free deposit" into a
                  premium plan. The booking flow uses these values instead of
                  the car-level baseline whenever this plan is selected. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Deposit (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="50"
                    className="mt-1 rounded-xl"
                    placeholder="0 = free deposit"
                    value={p.deposit ?? 0}
                    onChange={(e) => update(i, { deposit: e.target.value === "" ? 0 : Number(e.target.value) })}
                    data-testid={`insurance-deposit-${i}`}
                  />
                  <p className="text-[11px] text-neutral-500 mt-1">Charged at handover when this plan is selected. Set to 0 for "free deposit" upsells.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Excess (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="50"
                    className="mt-1 rounded-xl"
                    placeholder="0 = no excess"
                    value={p.excess ?? 0}
                    onChange={(e) => update(i, { excess: e.target.value === "" ? 0 : Number(e.target.value) })}
                    data-testid={`insurance-excess-${i}`}
                  />
                  <p className="text-[11px] text-neutral-500 mt-1">Damage excess remaining with the renter — informational, shown on the agreement.</p>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Coverage (comma-separated)</Label>
                <Input
                  className="mt-1 rounded-xl"
                  placeholder="e.g. Collision, Theft, Roadside assist"
                  value={Array.isArray(p.coverage) ? p.coverage.join(", ") : (p.coverage ?? "")}
                  onChange={(e) => update(i, { coverage: e.target.value })}
                  data-testid={`insurance-coverage-${i}`}
                  onBlur={(e) => update(i, { coverage: normalizeCoverage(e.target.value) })}
                />
                <p className="text-[11px] text-emerald-700 mt-1">Surfaces as a green ✓ strip under the plan card on the booking page.</p>
              </div>
              {/* Iter 67bu — Exclusions ("what's NOT covered"). Renders as a
                  red strip under the plan card so renters can't miss the gaps. */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-rose-700 font-semibold">Not covered (comma-separated)</Label>
                <Input
                  className="mt-1 rounded-xl"
                  placeholder="e.g. Tyres & rims, Off-road damage, Windscreen, Roof damage"
                  value={Array.isArray(p.exclusions) ? p.exclusions.join(", ") : (p.exclusions ?? "")}
                  onChange={(e) => update(i, { exclusions: e.target.value })}
                  data-testid={`insurance-exclusions-${i}`}
                  onBlur={(e) => update(i, { exclusions: normalizeCoverage(e.target.value) })}
                />
                <p className="text-[11px] text-rose-700 mt-1">Surfaces as a red ✕ strip under the plan card. Transparency reduces handover disputes.</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
